import { useEffect, useMemo, useRef, useState } from "react";
import { X, Search, Anchor, ShieldAlert, Ship, Scale, ArrowRightLeft, BookOpen, AlertTriangle, ExternalLink, CheckCircle2, MapPin, Building2, ListChecks, Plus, Trash2, ChevronDown, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { useLang, WA_LINK } from "../i18n";
import { PORTS, REGIONS } from "../data/ports";
import { MX_MINES, mxPos } from "../data/mexicoMines";
import { COMPANIES } from "../data/companies";

const TAB_ICONS = { ports: Anchor, mexico: MapPin, directory: Building2, minerals: Scale, transport: Ship, costs: ArrowRightLeft, regulations: ShieldAlert, compare: ArrowRightLeft, workspace: ListChecks, learn: BookOpen };
const PORTS_KEY = "jasanor";
const SECURE_MINERALS = ["gold", "silver", "pgm"];
const STATUS_COLORS = { operating: "text-[#4ADE80] border-[#4ADE80]/40 bg-[#4ADE80]/10", development: "text-[#C9A227] border-[#C9A227]/40 bg-[#C9A227]/10", exploration: "text-[#B8BEC7] border-[#B8BEC7]/40 bg-[#B8BEC7]/10", suspended: "text-[#F87171] border-[#F87171]/40 bg-[#F87171]/10", historical: "text-[#8A919B] border-[#8A919B]/40 bg-[#8A919B]/10" };
const ROLE_COLORS = { buyer: "text-[#4ADE80] border-[#4ADE80]/40 bg-[#4ADE80]/10", seller: "text-[#C9A227] border-[#C9A227]/40 bg-[#C9A227]/10", both: "text-[#B8BEC7] border-[#B8BEC7]/40 bg-[#B8BEC7]/10" };

function scorePort(port, mineralKey, qty) {
  let s = 0;
  if (port.minerals.includes(mineralKey)) s += 3;
  if (port.bulk) s += 1;
  if (port.container) s += 1;
  if (port.rail) s += 1;
  if (qty >= 2000 && port.bulk && port.deep) s += 1;
  if (qty < 500 && port.container) s += 1;
  return s;
}

export function PortsOverlay() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ports");
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("all");
  const [cmp, setCmp] = useState({ mineral: "copper", qty: 500, a: "callao", b: "rotterdam" });
  const [learnOpen, setLearnOpen] = useState(null);
  const [ws, setWs] = useState([]);
  const [mxQ, setMxQ] = useState("");
  const [mxState, setMxState] = useState("all");
  const [mxStatus, setMxStatus] = useState("all");
  const [mxMineral, setMxMineral] = useState("all");
  const [dirQ, setDirQ] = useState("");
  const [dirRole, setDirRole] = useState("all");
  const [mxOpen, setMxOpen] = useState(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", loop: true, dragFree: true });
  const [gate, setGate] = useState(false);
  const [gateKey, setGateKey] = useState("");
  const [gateError, setGateError] = useState(false);

  const gateRef = useRef({ n: 0, last: 0 });

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("mx:open-ports", handler);
    return () => window.removeEventListener("mx:open-ports", handler);
  }, []);

  useEffect(() => {
    const handler = () => {
      if (sessionStorage.getItem("mx-ports-unlocked") === "1") {
        window.dispatchEvent(new CustomEvent("mx:open-ports"));
        return;
      }
      const g = gateRef.current;
      const now = Date.now();
      if (now - g.last < 250) return;
      if (now - g.last > 1500) g.n = 0;
      g.last = now;
      g.n += 1;
      if (g.n >= 3) {
        g.n = 0;
        setGateKey("");
        setGateError(false);
        setGate(true);
        return;
      }
      // silent gate: no visual progress indicator
    };
    window.addEventListener("mx:ports-click", handler);
    return () => window.removeEventListener("mx:ports-click", handler);
  }, []);

  useEffect(() => {
    if (open || gate) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open, gate]);

  const p = t.ports;
  const it = t.intel;
  const inputCls = "rounded-lg border border-[#EDF1F6]/25 bg-[#07111F]/70 px-4 py-2.5 text-sm text-[#F5F7FA] outline-none focus:border-[#C9A227] transition-colors duration-300";

  const wsHas = (kind, id) => ws.some((w) => w.kind === kind && w.id === id);
  const toggleWs = (kind, id, label) =>
    setWs((prev) => (wsHas(kind, id) ? prev.filter((w) => !(w.kind === kind && w.id === id)) : [...prev, { kind, id, label }]));

  const submitGate = (e) => {
    e.preventDefault();
    if (gateKey.trim().toLowerCase() === PORTS_KEY) {
      sessionStorage.setItem("mx-ports-unlocked", "1");
      setGate(false);
      window.dispatchEvent(new CustomEvent("mx:open-ports"));
    } else {
      setGateError(true);
    }
  };

  const tabList = ["ports", "mexico", "directory", "minerals", "transport", "costs", "regulations", "compare", "workspace", "learn"];
  const tabLabel = (k) => (p.tabs[k] ? p.tabs[k] : it.tabsExtra[k]);

  const filteredPorts = useMemo(
    () => PORTS.filter((pt) => (region === "all" || pt.region === region) && (query === "" || `${pt.name} ${pt.country.en} ${pt.country.es}`.toLowerCase().includes(query.toLowerCase()))),
    [query, region]
  );

  const mxStates = useMemo(() => [...new Set(MX_MINES.map((m) => m.state))].sort(), []);
  const filteredMines = useMemo(
    () =>
      MX_MINES.filter(
        (m) =>
          (mxState === "all" || m.state === mxState) &&
          (mxStatus === "all" || m.status === mxStatus) &&
          (mxMineral === "all" || m.minerals.includes(mxMineral)) &&
          (mxQ === "" || `${m.name} ${m.operator} ${m.parent} ${m.state}`.toLowerCase().includes(mxQ.toLowerCase()))
      ),
    [mxQ, mxState, mxStatus, mxMineral]
  );

  const filteredCompanies = useMemo(
    () => COMPANIES.filter((c) => (dirRole === "all" || c.role === dirRole) && (dirQ === "" || `${c.name} ${c.country} ${c.industry.en} ${c.industry.es}`.toLowerCase().includes(dirQ.toLowerCase()))),
    [dirQ, dirRole]
  );

  if (!open && !gate) return null;

  const mineralKeys = Object.keys(p.mineralNames);
  const cmpPorts = [PORTS.find((x) => x.id === cmp.a), PORTS.find((x) => x.id === cmp.b)].filter(Boolean);
  const qty = Number(cmp.qty) || 0;
  const chain = SECURE_MINERALS.includes(cmp.mineral) ? p.compare.chainSecure : qty >= 2000 ? p.compare.chainBulk : qty >= 500 ? p.compare.chainMid : p.compare.chainSmall;
  const scores = cmpPorts.map((pt) => scorePort(pt, cmp.mineral, qty));
  const bestIdx = scores[0] === scores[1] ? -1 : scores.indexOf(Math.max(...scores));

  const wsButton = (kind, id, label, testId) => (
    <button
      data-testid={testId}
      onClick={() => toggleWs(kind, id, label)}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors duration-300 ${
        wsHas(kind, id) ? "border-[#4ADE80]/50 bg-[#4ADE80]/10 text-[#4ADE80]" : "border-[#C9A227]/40 text-[#C9A227] hover:bg-[#C9A227] hover:text-[#07111F]"
      }`}
    >
      <Plus size={11} /> {wsHas(kind, id) ? it.mx.addedWs : it.mx.addWs}
    </button>
  );

  return (
    <>
    {gate && (
      <div className="fixed inset-0 z-[260] flex items-center justify-center p-5" role="dialog" aria-modal="true" aria-label={p.gate.title} data-testid="ports-gate-modal">
        <div className="absolute inset-0 bg-[#050C16]/92 backdrop-blur-sm" onClick={() => setGate(false)} />
        <form onSubmit={submitGate} className="relative glass w-full max-w-sm p-8" style={{ borderRadius: "4px 24px 4px 24px" }}>
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#C9A227]/40 bg-[#C9A227]/10 text-[#C9A227]">
            <Lock size={20} />
          </span>
          <h3 className="mt-5 text-center font-display text-2xl text-[#F5F7FA]">{p.gate.title}</h3>
          <label className="mt-6 block text-xs uppercase tracking-wider text-[#EDF1F6]/75" htmlFor="ports-gate-key">{p.gate.label}</label>
          <input
            id="ports-gate-key"
            data-testid="ports-gate-input"
            type="password"
            autoFocus
            autoComplete="off"
            value={gateKey}
            onChange={(e) => {
              setGateKey(e.target.value);
              setGateError(false);
            }}
            placeholder={p.gate.placeholder}
            className={`${inputCls} mt-2 w-full`}
          />
          {gateError && (
            <p className="mt-2 text-xs text-[#F87171]" data-testid="ports-gate-error">{p.gate.error}</p>
          )}
          <div className="mt-6 flex gap-3">
            <button type="submit" data-testid="ports-gate-submit" className="flex-1 rounded-full bg-[#C9A227] px-5 py-2.5 text-xs font-bold tracking-widest text-[#07111F] hover:bg-[#F5F7FA] transition-colors duration-300">
              {p.gate.submit}
            </button>
            <button type="button" data-testid="ports-gate-cancel" onClick={() => setGate(false)} className="rounded-full border border-[#EDF1F6]/30 px-5 py-2.5 text-xs font-semibold tracking-widest text-[#F5F7FA] hover:border-[#C9A227] hover:text-[#C9A227] transition-colors duration-300">
              {p.gate.cancel}
            </button>
          </div>
        </form>
      </div>
    )}
    {open && (
    <div className="fixed inset-0 z-[250] flex flex-col bg-[#050C16]" data-testid="ports-overlay" role="dialog" aria-modal="true" aria-label={it.intelTitle}>
      <header className="flex items-center justify-between gap-4 border-b border-[#C9A227]/20 bg-[#07111F]/90 px-5 md:px-8 py-4 backdrop-blur-xl">
        <div className="flex items-center gap-3 min-w-0">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#C9A227]/40 bg-[#C9A227]/10 text-[#C9A227]">
            <Anchor size={19} />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg md:text-2xl text-[#F5F7FA] truncate" data-testid="ports-title">{it.intelTitle}</h2>
            <p className="hidden md:block text-xs text-[#EDF1F6]/80 truncate">{p.subtitle}</p>
          </div>
        </div>
        <button
          data-testid="ports-close-button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#EDF1F6]/25 text-[#EDF1F6] hover:border-[#C9A227] hover:text-[#C9A227] transition-colors duration-300"
        >
          <X size={18} />
        </button>
      </header>

      <nav className="no-scrollbar flex gap-2 overflow-x-auto border-b border-white/5 bg-[#07111F]/60 px-5 md:px-8 py-3" role="tablist">
        {tabList.map((k) => {
          const Icon = TAB_ICONS[k];
          return (
            <button
              key={k}
              data-testid={`ports-tab-${k}`}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setTab(k)}
              className={`relative flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold tracking-wider transition-[background-color,color] duration-300 ${
                tab === k ? "bg-[#C9A227] text-[#07111F]" : "border border-[#EDF1F6]/20 text-[#EDF1F6] hover:border-[#C9A227]/50"
              }`}
            >
              <Icon size={13} /> {tabLabel(k)}
              {k === "workspace" && ws.length > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-[#07111F] text-[9px] font-bold text-[#C9A227]" data-testid="workspace-count">{ws.length}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div className="flex-1 overflow-y-auto px-5 md:px-8 py-8" data-testid="ports-content">
        <div className="mx-auto max-w-6xl">
          <div className="mb-6 flex items-start gap-3 border border-[#C9A227]/25 bg-[#C9A227]/5 p-4" style={{ borderRadius: "4px 16px 4px 16px" }}>
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-[#C9A227]" />
            <p className="text-xs text-[#EDF1F6] leading-relaxed">{p.eduNote}</p>
          </div>

          {tab === "ports" && (
            <div>
              <div className="mb-6 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#EDF1F6]/60" />
                  <input data-testid="ports-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={p.search} className={`${inputCls} w-full pl-11`} />
                </div>
                <select data-testid="ports-region-filter" value={region} onChange={(e) => setRegion(e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="all" className="bg-[#151B24]">{p.regionAll}</option>
                  {REGIONS.map((r) => (
                    <option key={r} value={r} className="bg-[#151B24]">{p.regions[r]}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4 flex justify-end gap-3">
                <button data-testid="ports-prev-btn" aria-label="Previous ports" onClick={() => emblaApi?.scrollPrev()} className="rounded-full border border-[#EDF1F6]/25 p-2.5 text-[#EDF1F6] hover:border-[#C9A227] hover:text-[#C9A227] transition-colors duration-300">
                  <ChevronLeft size={18} />
                </button>
                <button data-testid="ports-next-btn" aria-label="Next ports" onClick={() => emblaApi?.scrollNext()} className="rounded-full border border-[#EDF1F6]/25 p-2.5 text-[#EDF1F6] hover:border-[#C9A227] hover:text-[#C9A227] transition-colors duration-300">
                  <ChevronRight size={18} />
                </button>
              </div>
              <div className="overflow-hidden" ref={emblaRef} data-testid="ports-carousel">
              <div className="flex gap-4 py-2">
                {filteredPorts.map((pt) => (
                  <article key={pt.id} data-testid={`port-card-${pt.id}`} className="min-w-[320px] md:min-w-[430px] glass p-6 transition-[border-color] duration-300 hover:border-[#C9A227]/40" style={{ borderRadius: "4px 20px 4px 20px" }}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-lg text-[#F5F7FA]">{pt.name}</h3>
                        <p className="mt-1 text-xs text-[#EDF1F6]/80">{pt.country[lang]} · {p.regions[pt.region]}</p>
                      </div>
                      <a href={pt.link} target="_blank" rel="noopener noreferrer" className="shrink-0 text-[#C9A227] hover:text-[#F5F7FA] transition-colors" aria-label={p.moreInfo} data-testid={`port-link-${pt.id}`}>
                        <ExternalLink size={16} />
                      </a>
                    </div>
                    <p className="mt-3 text-sm text-[#EDF1F6]">{pt.type[lang]}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {Object.keys(p.caps).map((c) =>
                        pt[c] ? (
                          <span key={c} className="rounded-full border border-[#C9A227]/30 bg-[#C9A227]/8 px-2.5 py-1 text-[10px] font-medium text-[#EDF1F6]">{p.caps[c]}</span>
                        ) : null
                      )}
                    </div>
                    <div className="mt-4 space-y-2 text-xs">
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{p.focus}:</span> <span className="text-[#EDF1F6]">{pt.focus[lang]}</span></p>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{p.routes}:</span> <span className="text-[#EDF1F6]">{pt.routes[lang]}</span></p>
                    </div>
                    <div className="mt-4 border-t border-white/5 pt-3">
                      <p className="text-[10px] uppercase tracking-wider text-[#EDF1F6]/70">{p.specsTitle}</p>
                      <p className="mt-1 text-[11px] italic text-[#EDF1F6]/70">{p.specsNote}</p>
                    </div>
                    <div className="mt-4">{wsButton("port", pt.id, pt.name, `ws-add-port-${pt.id}`)}</div>
                  </article>
                ))}
              </div>
              </div>
            </div>
          )}

          {tab === "mexico" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]" data-testid="mexico-title">{it.mx.title}</h3>
              <p className="mt-1 mb-5 text-sm text-[#EDF1F6]">{it.mx.subtitle}</p>

              <div className="relative mb-2 overflow-hidden border border-[#C9A227]/20" style={{ borderRadius: "4px 24px 4px 24px" }} data-testid="mexico-map">
                <img src="/assets/mexico-map.webp" alt="Mexico map" loading="lazy" className="w-full object-cover" />
                {filteredMines.map((m) => {
                  const pos = mxPos(m.lat, m.lon);
                  return (
                    <span
                      key={m.id}
                      title={`${m.name} — ${m.state}`}
                      className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#C9A227] shadow-[0_0_10px_rgba(201,162,39,0.9)] ring-2 ring-[#07111F]/60"
                      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                    />
                  );
                })}
              </div>
              <p className="mb-5 text-[11px] italic text-[#EDF1F6]/70">{it.mx.mapNote}</p>

              <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="relative">
                  <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#EDF1F6]/60" />
                  <input data-testid="mexico-search" value={mxQ} onChange={(e) => setMxQ(e.target.value)} placeholder={it.mx.search} className={`${inputCls} w-full pl-10`} />
                </div>
                <select data-testid="mexico-state-filter" value={mxState} onChange={(e) => setMxState(e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="all" className="bg-[#151B24]">{it.mx.stateAll}</option>
                  {mxStates.map((s) => (
                    <option key={s} value={s} className="bg-[#151B24]">{s}</option>
                  ))}
                </select>
                <select data-testid="mexico-status-filter" value={mxStatus} onChange={(e) => setMxStatus(e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="all" className="bg-[#151B24]">{it.mx.statusAll}</option>
                  {Object.keys(it.statusLabels).map((s) => (
                    <option key={s} value={s} className="bg-[#151B24]">{it.statusLabels[s]}</option>
                  ))}
                </select>
                <select data-testid="mexico-mineral-filter" value={mxMineral} onChange={(e) => setMxMineral(e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="all" className="bg-[#151B24]">{it.mx.mineralAll}</option>
                  {mineralKeys.map((k) => (
                    <option key={k} value={k} className="bg-[#151B24]">{p.mineralNames[k]}</option>
                  ))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {filteredMines.map((m) => (
                  <article key={m.id} data-testid={`mine-card-${m.id}`} className="glass p-6 transition-[border-color] duration-300 hover:border-[#C9A227]/40" style={{ borderRadius: "4px 20px 4px 20px" }}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="font-display text-lg text-[#F5F7FA]">{m.name}</h4>
                        <p className="mt-1 text-xs text-[#EDF1F6]/80">{m.state}, {lang === "es" ? "México" : "Mexico"} · {it.typeLabels[m.type]}</p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${STATUS_COLORS[m.status]}`} data-testid={`mine-status-${m.id}`}>
                          {it.statusLabels[m.status]}
                        </span>
                        <button
                          data-testid={`mine-expand-${m.id}`}
                          onClick={() => setMxOpen(mxOpen === m.id ? null : m.id)}
                          aria-expanded={mxOpen === m.id}
                          aria-label={it.mx.details}
                          title={it.mx.details}
                          className="flex h-7 w-7 items-center justify-center rounded-full border border-[#C9A227]/40 text-[#C9A227] hover:bg-[#C9A227] hover:text-[#07111F] transition-colors duration-300"
                        >
                          <ChevronDown size={14} className={`transition-transform duration-300 ${mxOpen === m.id ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    </div>
                    <div className="mt-4 space-y-2 text-xs">
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{it.mx.operator}:</span> <span className="text-[#EDF1F6]">{m.operator}</span></p>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{it.mx.parent}:</span> <span className="text-[#EDF1F6]">{m.parent}</span></p>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{it.mx.product}:</span> <span className="text-[#EDF1F6]">{m.product[lang]}</span></p>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{it.mx.nearestPort}:</span> <span className="text-[#EDF1F6]">{m.portText[lang]}</span></p>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {m.minerals.map((k) => (
                        <span key={k} className="rounded-full border border-[#EDF1F6]/20 px-2.5 py-1 text-[10px] text-[#EDF1F6]">{p.mineralNames[k] || k}</span>
                      ))}
                    </div>
                    {mxOpen === m.id && (
                      <div className="mt-4 space-y-3 border-t border-[#C9A227]/15 pt-4" data-testid={`mine-detail-${m.id}`}>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A227]">{it.mx.details}</p>
                        {m.minerals.map((k) => {
                          const r = p.mineralRows[k];
                          if (!r) return null;
                          return (
                            <div key={k} className="border border-white/5 bg-white/[0.02] p-4" style={{ borderRadius: "4px 14px 4px 14px" }}>
                              <p className="text-xs font-semibold uppercase tracking-wider text-[#F5F7FA]">{p.mineralNames[k] || k}</p>
                              <div className="mt-2 space-y-1.5 text-xs text-[#EDF1F6]">
                                <p><span className="uppercase tracking-wider text-[#C9A227]/90">{p.colForm}:</span> {r.form}</p>
                                <p><span className="uppercase tracking-wider text-[#C9A227]/90">{p.colTransport}:</span> {r.transport}</p>
                                <p><span className="uppercase tracking-wider text-[#C9A227]/90">{p.colPackaging}:</span> {r.packaging}</p>
                                <p><span className="uppercase tracking-wider text-[#C9A227]/90">{p.colDocs}:</span> {r.docs}</p>
                                <p className="italic text-[#EDF1F6]/85">{r.notes}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-4 border-t border-white/5 pt-3">
                      <p className="text-[10px] uppercase tracking-wider text-[#EDF1F6]/70">{it.mx.permits}</p>
                      <p className="mt-1 text-[11px] italic text-[#EDF1F6]/70">{it.mx.permitsNote}</p>
                      <p className="mt-2 text-[10px] uppercase tracking-wider text-[#EDF1F6]/60">{it.mx.sourceLine}</p>
                    </div>
                    <div className="mt-4">{wsButton("mine", m.id, `${m.name} (${m.state})`, `ws-add-mine-${m.id}`)}</div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {tab === "directory" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]" data-testid="directory-title">{it.dir.title}</h3>
              <p className="mt-1 mb-5 text-sm text-[#EDF1F6]">{it.dir.subtitle}</p>
              <div className="mb-6 flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#EDF1F6]/60" />
                  <input data-testid="directory-search" value={dirQ} onChange={(e) => setDirQ(e.target.value)} placeholder={it.dir.search} className={`${inputCls} w-full pl-10`} />
                </div>
                <div className="flex gap-2">
                  {["all", "buyer", "seller", "both"].map((r) => (
                    <button
                      key={r}
                      data-testid={`directory-filter-${r}`}
                      onClick={() => setDirRole(r)}
                      className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-300 ${dirRole === r ? "bg-[#C9A227] text-[#07111F]" : "border border-[#EDF1F6]/20 text-[#EDF1F6] hover:border-[#C9A227]/50"}`}
                    >
                      {r === "all" ? it.dir.all : it.roleLabels[r]}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCompanies.map((c) => (
                  <article key={c.id} data-testid={`company-card-${c.id}`} className="glass p-5 flex flex-col transition-[border-color] duration-300 hover:border-[#C9A227]/40" style={{ borderRadius: "4px 16px 4px 16px" }}>
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-display text-base text-[#F5F7FA]">{c.name}</h4>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${ROLE_COLORS[c.role]}`}>{it.roleLabels[c.role]}</span>
                    </div>
                    <p className="mt-1 text-xs text-[#EDF1F6]/80">{c.country} · {c.industry[lang]}</p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {c.minerals.map((k) => (
                        <span key={k} className="rounded-full border border-[#EDF1F6]/20 px-2 py-0.5 text-[10px] text-[#EDF1F6]">{p.mineralNames[k] || k}</span>
                      ))}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-white/5 pt-3">
                      <a href={c.site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#C9A227] hover:text-[#F5F7FA] transition-colors" data-testid={`company-site-${c.id}`}>
                        <ExternalLink size={11} /> {it.dir.website}
                      </a>
                      <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#EDF1F6] hover:text-[#C9A227] transition-colors" data-testid={`company-info-${c.id}`}>
                        {it.dir.requestInfo}
                      </a>
                    </div>
                    <div className="mt-3">{wsButton("company", c.id, c.name, `ws-add-company-${c.id}`)}</div>
                  </article>
                ))}
              </div>
              <p className="mt-6 text-[11px] italic text-[#EDF1F6]/70 leading-relaxed">{it.dir.note}</p>
            </div>
          )}

          {tab === "minerals" && (
            <div>
              <p className="mb-6 text-sm text-[#EDF1F6]">{p.mineralsIntro}</p>
              <div className="glass overflow-x-auto no-scrollbar" style={{ borderRadius: "4px 20px 4px 20px" }}>
                <table className="w-full min-w-[860px] text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-[#EDF1F6]/75 border-b border-[#C9A227]/15">
                      <th className="px-4 py-3 font-medium">{t.prices.material}</th>
                      <th className="px-4 py-3 font-medium">{p.colForm}</th>
                      <th className="px-4 py-3 font-medium">{p.colTransport}</th>
                      <th className="px-4 py-3 font-medium">{p.colPackaging}</th>
                      <th className="px-4 py-3 font-medium">{p.colDocs}</th>
                      <th className="px-4 py-3 font-medium">{p.colNotes}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mineralKeys.map((k) => {
                      const r = p.mineralRows[k];
                      if (!r) return null;
                      return (
                        <tr key={k} className="border-t border-white/5 align-top" data-testid={`cargo-row-${k}`}>
                          <td className="px-4 py-3.5 font-medium text-[#C9A227]">{p.mineralNames[k]}</td>
                          <td className="px-4 py-3.5 text-[#EDF1F6]">{r.form}</td>
                          <td className="px-4 py-3.5 text-[#EDF1F6]">{r.transport}</td>
                          <td className="px-4 py-3.5 text-[#EDF1F6]">{r.packaging}</td>
                          <td className="px-4 py-3.5 text-[#EDF1F6]">{r.docs}</td>
                          <td className="px-4 py-3.5 text-[#EDF1F6]/85 text-xs italic">{r.notes}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === "transport" && (
            <div>
              <p className="mb-6 text-sm text-[#EDF1F6]">{p.transportIntro}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {p.transportCards.map((c, i) => (
                  <div key={i} className="glass p-6" style={{ borderRadius: "4px 20px 4px 20px" }} data-testid={`transport-card-${i}`}>
                    <h3 className="font-display text-base text-[#F5F7FA]">{c.name}</h3>
                    <p className="mt-3 text-xs text-[#4ADE80]">+ {c.pros}</p>
                    <p className="mt-1.5 text-xs text-[#F87171]">− {c.cons}</p>
                    <p className="mt-3 text-xs text-[#EDF1F6]"><span className="text-[#C9A227]">→</span> {c.use}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "costs" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]">{p.costsTitle}</h3>
              <p className="mt-2 mb-6 text-sm text-[#EDF1F6]">{p.costsIntro}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {p.costComponents.map((c, i) => (
                  <div key={i} className="glass p-5" style={{ borderRadius: "4px 16px 4px 16px" }} data-testid={`cost-card-${i}`}>
                    <h4 className="text-sm font-semibold text-[#F5F7FA]">{c.name}</h4>
                    <p className="mt-1.5 text-xs text-[#EDF1F6]/85">{c.what}</p>
                    <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#C9A227]/30 px-2.5 py-1 text-[10px] italic text-[#C9A227]">{p.costVerify}</p>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-xs text-[#EDF1F6]/80 italic">{p.transit}</p>
              <p className="mt-4 text-xs text-[#EDF1F6]">
                {p.costSources}:{" "}
                <a href="https://www.balticexchange.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#C9A227] hover:text-[#F5F7FA] transition-colors" data-testid="baltic-link">
                  {p.baltic} <ExternalLink size={11} />
                </a>
              </p>
            </div>
          )}

          {tab === "regulations" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]">{p.regTitle}</h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {p.regCards.map((c, i) => (
                  <div key={i} className="glass p-5 flex gap-3" style={{ borderRadius: "4px 16px 4px 16px" }} data-testid={`reg-card-${i}`}>
                    <ShieldAlert size={17} className="mt-0.5 shrink-0 text-[#C9A227]" />
                    <div>
                      <h4 className="text-sm font-semibold text-[#F5F7FA]">{c.name}</h4>
                      <p className="mt-1 text-xs text-[#EDF1F6]/85 leading-relaxed">{c.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex items-start gap-3 border border-[#F87171]/30 bg-[#F87171]/5 p-5" style={{ borderRadius: "4px 16px 4px 16px" }} data-testid="ports-reg-warning">
                <AlertTriangle size={17} className="mt-0.5 shrink-0 text-[#F87171]" />
                <p className="text-xs text-[#EDF1F6] leading-relaxed">{p.regWarning}</p>
              </div>
            </div>
          )}

          {tab === "compare" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]">{p.compare.title}</h3>
              <div className="mt-6 glass p-5 flex flex-wrap items-end gap-3" style={{ borderRadius: "4px 20px 4px 20px" }} data-testid="compare-controls">
                <label className="flex flex-col gap-1.5 text-xs text-[#EDF1F6]/80">
                  {p.compare.mineral}
                  <select data-testid="compare-mineral" value={cmp.mineral} onChange={(e) => setCmp({ ...cmp, mineral: e.target.value })} className={`${inputCls} appearance-none`}>
                    {mineralKeys.map((k) => (
                      <option key={k} value={k} className="bg-[#151B24]">{p.mineralNames[k]}</option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5 text-xs text-[#EDF1F6]/80">
                  {p.compare.qty}
                  <input data-testid="compare-qty" type="number" min="1" value={cmp.qty} onChange={(e) => setCmp({ ...cmp, qty: e.target.value })} className={`${inputCls} w-32`} />
                </label>
                {["a", "b"].map((slot) => (
                  <label key={slot} className="flex flex-col gap-1.5 text-xs text-[#EDF1F6]/80">
                    {slot === "a" ? p.compare.portA : p.compare.portB}
                    <select data-testid={`compare-port-${slot}`} value={cmp[slot]} onChange={(e) => setCmp({ ...cmp, [slot]: e.target.value })} className={`${inputCls} appearance-none`}>
                      {PORTS.map((pt) => (
                        <option key={pt.id} value={pt.id} className="bg-[#151B24]">{pt.name}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>

              <div className="mt-6 grid md:grid-cols-2 gap-4">
                {cmpPorts.map((pt, i) => (
                  <div key={pt.id} className={`glass p-6 relative ${bestIdx === i ? "border-[#C9A227]/60 shadow-[0_0_40px_rgba(201,162,39,0.12)]" : ""}`} style={{ borderRadius: "4px 20px 4px 20px" }} data-testid={`compare-card-${i}`}>
                    {bestIdx === i && (
                      <span className="absolute -top-3 left-5 inline-flex items-center gap-1.5 rounded-full bg-[#C9A227] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#07111F]" data-testid="compare-best-badge">
                        <CheckCircle2 size={11} /> {p.compare.best}
                      </span>
                    )}
                    <h4 className="font-display text-lg text-[#F5F7FA]">{pt.name}</h4>
                    <p className="mt-1 text-xs text-[#EDF1F6]/80">{pt.country[lang]} · {p.regions[pt.region]}</p>
                    <div className="mt-4 space-y-3 text-xs">
                      <div>
                        <p className="uppercase tracking-wider text-[#C9A227]">{p.compare.score}</p>
                        <div className="mt-1.5 h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
                          <div className="h-full rounded-full bg-gradient-to-r from-[#C9A227] to-[#F5E6B8] transition-[width] duration-700" style={{ width: `${Math.min(100, (scores[i] / 7) * 100)}%` }} />
                        </div>
                      </div>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{p.compare.chain}:</span> <span className="text-[#EDF1F6]">{chain}</span></p>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{p.compare.cost}:</span> <span className="italic text-[#EDF1F6]/85">{p.costVerify}</span></p>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{p.compare.transit}:</span> <span className="italic text-[#EDF1F6]/85">{p.unavailable}</span></p>
                      <div>
                        <p className="uppercase tracking-wider text-[#C9A227]">{p.compare.requirements}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          {p.compare.reqItems.map((r) => (
                            <span key={r} className="rounded-full border border-[#EDF1F6]/20 px-2.5 py-1 text-[10px] text-[#EDF1F6]">{r}</span>
                          ))}
                        </div>
                      </div>
                      <p><span className="uppercase tracking-wider text-[#C9A227]">{p.compare.risks}:</span> <span className="text-[#EDF1F6]/85">{p.mineralRows[cmp.mineral] ? p.mineralRows[cmp.mineral].notes : p.unavailable}</span></p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "workspace" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]" data-testid="workspace-title">{it.ws.title}</h3>
              <p className="mt-1 mb-6 text-sm text-[#EDF1F6]">{it.ws.subtitle}</p>
              {ws.length === 0 ? (
                <p className="glass p-6 text-sm italic text-[#EDF1F6]/80" style={{ borderRadius: "4px 16px 4px 16px" }} data-testid="workspace-empty">{it.ws.empty}</p>
              ) : (
                <div className="space-y-3" data-testid="workspace-list">
                  {ws.map((w) => (
                    <div key={`${w.kind}-${w.id}`} className="glass flex items-center justify-between gap-3 px-5 py-3.5" style={{ borderRadius: "4px 14px 4px 14px" }}>
                      <div className="flex items-center gap-3">
                        <span className="rounded-full border border-[#C9A227]/40 px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#C9A227]">{it.ws.types[w.kind]}</span>
                        <span className="text-sm text-[#F5F7FA]">{w.label}</span>
                      </div>
                      <button onClick={() => toggleWs(w.kind, w.id, w.label)} aria-label={it.ws.remove} className="text-[#EDF1F6]/60 hover:text-[#F87171] transition-colors" data-testid={`ws-remove-${w.kind}-${w.id}`}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  <button onClick={() => setWs([])} className="text-xs text-[#EDF1F6]/70 hover:text-[#F87171] transition-colors" data-testid="workspace-clear">{it.ws.clear}</button>
                </div>
              )}
              <div className="mt-8">
                <h4 className="font-display text-base text-[#F5F7FA]">{it.ws.questionsTitle}</h4>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2.5">
                  {it.ws.questions.map((q, i) => (
                    <li key={i} className="glass px-4 py-3 text-xs text-[#EDF1F6] flex gap-2.5" style={{ borderRadius: "4px 12px 4px 12px" }} data-testid={`ws-question-${i + 1}`}>
                      <span className="text-[#C9A227]" aria-hidden="true">◆</span> {q}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {tab === "learn" && (
            <div>
              <h3 className="font-display text-xl text-[#F5F7FA]">{p.learnTitle}</h3>
              <div className="mt-6 space-y-3">
                {p.learn.map((item, i) => (
                  <div key={i} className="glass overflow-hidden" style={{ borderRadius: "4px 16px 4px 16px" }} data-testid={`learn-item-${i}`}>
                    <button onClick={() => setLearnOpen(learnOpen === i ? null : i)} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium text-[#F5F7FA] hover:text-[#C9A227] transition-colors duration-300" aria-expanded={learnOpen === i}>
                      {item.q}
                      <span className={`text-[#C9A227] transition-transform duration-300 ${learnOpen === i ? "rotate-45" : ""}`}>+</span>
                    </button>
                    {learnOpen === i && <p className="px-5 pb-4 text-sm text-[#EDF1F6]/90 leading-relaxed">{item.a}</p>}
                  </div>
                ))}
              </div>
              <h3 className="mt-10 font-display text-xl text-[#F5F7FA]">{it.learn2.title}</h3>
              <div className="mt-6 grid sm:grid-cols-2 gap-4">
                {it.learn2.sections.map((s, i) => (
                  <div key={i} className="glass p-5" style={{ borderRadius: "4px 16px 4px 16px" }} data-testid={`learn2-${i}`}>
                    <h4 className="text-sm font-semibold uppercase tracking-wider text-[#C9A227]">{s.q}</h4>
                    <p className="mt-2 text-xs text-[#EDF1F6]/90 leading-relaxed">{s.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    )}
    </>
  );
}
